import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, Image, Animated } from 'react-native';
import {
  Text,
  Card,
  Button,
  IconButton,
  useTheme,
  Chip,
} from 'react-native-paper';
import {
  Clock,
  X,
  ThumbsUp,
  Play,
  Coffee,
  BookOpen,
  Brain,
  TrendingUp,
  MoreHorizontal,
} from 'lucide-react-native';
import type {
  ContentRecommendation,
  RecommendationFeedback,
} from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { RecommendationExplanation } from './RecommendationExplanation';
import { RecommendationAnalytics } from './RecommendationAnalytics';
import { logger } from '../../utils/logger';

interface AIRecommendationCardProps {
  recommendation: ContentRecommendation;
  onAccept: () => void;
  onDismiss: (reason?: string) => void;
  onFeedback: (feedback: RecommendationFeedback) => void;
  onPreferenceUpdate?: (preferences: RecommendationPreferences) => void;
  showAnalytics?: boolean;
  analyticsData?: RecommendationAnalyticsData;
}

interface RecommendationPreferences {
  contentTypes: string[];
  difficultyRange: [number, number];
  topicInterests: string[];
  timePreferences: number[];
}

interface RecommendationAnalyticsData {
  viewCount: number;
  acceptanceRate: number;
  averageRating: number;
  similarUserEngagement: number;
  performanceScore: number;
}

export function AIRecommendationCard({
  recommendation,
  onAccept,
  onDismiss,
  onFeedback,
  onPreferenceUpdate,
  showAnalytics = false,
  analyticsData,
}: AIRecommendationCardProps) {
  const theme = useTheme();
  const [showExplanation, setShowExplanation] = useState(false);
  const [isAnalyticsVisible, setIsAnalyticsVisible] = useState(showAnalytics);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [dismissalReason, setDismissalReason] = useState<string | null>(null);
  const [interactionStartTime] = useState(Date.now());
  const fadeAnim = useState(new Animated.Value(1))[0];

  // Track view time for analytics
  useEffect(() => {
    const viewStartTime = Date.now();
    return () => {
      const viewDuration = Date.now() - viewStartTime;
      // Track view duration for analytics
      if (viewDuration > 1000) {
        // Only track if viewed for more than 1 second
        trackRecommendationView(recommendation.id, viewDuration);
      }
    };
  }, [recommendation.id]);

  // Get icon based on content type
  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case 'recipe':
        return Coffee;
      case 'video':
        return Play;
      case 'article':
        return BookOpen;
      case 'quiz':
        return Brain;
      default:
        return BookOpen;
    }
  };

  const ContentIcon = getContentIcon(recommendation.contentType);

  // Get difficulty color
  const getDifficultyColor = (level: number) => {
    if (level <= 0.3) return '#4ECDC4'; // Easy - Teal
    if (level <= 0.7) return '#FFC107'; // Medium - Amber
    return '#E74C3C'; // Hard - Red
  };

  const difficultyColor = getDifficultyColor(recommendation.difficultyLevel);
  const difficultyLabel =
    recommendation.difficultyLevel <= 0.3
      ? 'EASY'
      : recommendation.difficultyLevel <= 0.7
        ? 'MEDIUM'
        : 'HARD';

  // Get performance color based on score
  const getPerformanceColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4'; // High performance - Teal
    if (score >= 0.6) return '#FF6B35'; // Medium performance - Orange
    return '#E74C3C'; // Low performance - Red
  };

  // Format estimated time
  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  // Analytics tracking function
  const trackRecommendationView = useCallback(
    (recommendationId: string, viewDuration: number) => {
      // This would typically call an analytics service
      logger.log(
        `Recommendation ${recommendationId} viewed for ${viewDuration}ms`
      );
    },
    []
  );

  const trackRecommendationInteraction = useCallback(
    (
      recommendationId: string,
      interactionType: string,
      metadata?: Record<string, any>
    ) => {
      // This would typically call an analytics service
      logger.log(
        `Recommendation ${recommendationId} interaction: ${interactionType}`,
        metadata
      );
    },
    []
  );

  const handleFeedback = useCallback(
    (type: 'positive' | 'negative', rating?: number, reason?: string) => {
      const interactionDuration = Date.now() - interactionStartTime;

      const feedback: RecommendationFeedback = {
        id: `feedback_${Date.now()}`,
        recommendationId: recommendation.id,
        userId: recommendation.userId,
        feedbackType: type === 'positive' ? 'accepted' : 'dismissed',
        rating: rating || (type === 'positive' ? 5 : 1),
        reason: reason || '',
        timestamp: new Date().toISOString(),
        context: {
          contentType: recommendation.contentType,
          confidence: recommendation.confidence,
          showedExplanation: showExplanation,
          showedAnalytics: isAnalyticsVisible,
          interactionDuration,
          dismissalReason,
        },
      };

      // Track interaction for analytics
      trackRecommendationInteraction(
        recommendation.id,
        type === 'positive' ? 'accepted' : 'dismissed',
        {
          rating,
          reason,
          interactionDuration,
          confidence: recommendation.confidence,
        }
      );

      onFeedback(feedback);
      setFeedbackGiven(true);

      // Animate card fade out
      Animated.timing(fadeAnim, {
        toValue: 0.7,
        duration: 300,
        useNativeDriver: true,
      }).start();

      if (type === 'positive') {
        setTimeout(() => onAccept(), 300);
      } else {
        setTimeout(() => onDismiss(reason), 300);
      }
    },
    [
      interactionStartTime,
      recommendation.id,
      recommendation.userId,
      recommendation.contentType,
      recommendation.confidence,
      showExplanation,
      isAnalyticsVisible,
      dismissalReason,
      trackRecommendationInteraction,
      onFeedback,
      fadeAnim,
      onAccept,
      onDismiss,
    ]
  );

  const handleDismissWithReason = useCallback(
    (reason: string) => {
      setDismissalReason(reason);
      handleFeedback('negative', 1, reason);
    },
    [handleFeedback]
  );

  const handleExplanationToggle = useCallback(() => {
    const newShowExplanation = !showExplanation;
    setShowExplanation(newShowExplanation);

    trackRecommendationInteraction(recommendation.id, 'explanation_toggled', {
      shown: newShowExplanation,
    });
  }, [showExplanation, recommendation.id, trackRecommendationInteraction]);

  const handleAnalyticsToggle = useCallback(() => {
    const newShowAnalytics = !isAnalyticsVisible;
    setIsAnalyticsVisible(newShowAnalytics);

    trackRecommendationInteraction(recommendation.id, 'analytics_toggled', {
      shown: newShowAnalytics,
    });
  }, [isAnalyticsVisible, recommendation.id, trackRecommendationInteraction]);

  return (
    <Animated.View style={{ opacity: fadeAnim }}>
      <Card
        style={[styles.container, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <ContentIcon size={20} color={theme.colors.primary} />
              <Text
                variant='labelMedium'
                style={[styles.contentType, { color: theme.colors.primary }]}
              >
                AI RECOMMENDED {recommendation.contentType.toUpperCase()}
              </Text>
              {analyticsData && (
                <Chip
                  mode='outlined'
                  textStyle={styles.performanceChipText}
                  style={[
                    styles.performanceChip,
                    {
                      borderColor: getPerformanceColor(
                        analyticsData.performanceScore
                      ),
                    },
                  ]}
                >
                  {Math.round(analyticsData.performanceScore * 100)}% match
                </Chip>
              )}
            </View>

            <View style={styles.headerRight}>
              {isAnalyticsVisible && analyticsData && (
                <IconButton
                  icon={MoreHorizontal}
                  size={20}
                  onPress={handleAnalyticsToggle}
                  style={styles.actionIcon}
                />
              )}
              <IconButton
                icon={X}
                size={20}
                onPress={() => handleDismissWithReason('not_interested')}
                style={styles.dismissButton}
              />
            </View>
          </View>

          {/* Content */}
          <View style={styles.contentContainer}>
            {/* Thumbnail */}
            {recommendation.thumbnailUrl && (
              <Image
                source={{ uri: recommendation.thumbnailUrl }}
                style={styles.thumbnail}
                resizeMode='cover'
              />
            )}

            <View style={styles.textContent}>
              <Text
                variant='titleMedium'
                style={[styles.title, { color: theme.colors.onSurface }]}
                numberOfLines={2}
              >
                {recommendation.title}
              </Text>

              <Text
                variant='bodySmall'
                style={[
                  styles.description,
                  { color: theme.colors.onSurfaceVariant },
                ]}
                numberOfLines={3}
              >
                {recommendation.description}
              </Text>
            </View>
          </View>

          {/* Metadata */}
          <View style={styles.metadata}>
            <View style={styles.metadataLeft}>
              <View style={styles.metadataItem}>
                <Clock size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.metadataText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatTime(recommendation.estimatedMinutes)}
                </Text>
              </View>

              <Chip
                mode='outlined'
                textStyle={[styles.difficultyText, { color: difficultyColor }]}
                style={[
                  styles.difficultyChip,
                  {
                    borderColor: difficultyColor,
                    backgroundColor: (difficultyColor as string) + '20',
                  },
                ]}
              >
                {difficultyLabel}
              </Chip>

              <Button
                mode='text'
                onPress={handleExplanationToggle}
                style={styles.inlineWhyToggle}
                labelStyle={styles.inlineWhyToggleLabel}
                compact
              >
                {showExplanation ? 'Hide' : 'Why this recommendation?'}
              </Button>
            </View>

            <ConfidenceIndicator
              confidence={recommendation.confidence}
              size='small'
              showLabel={false}
            />
          </View>

          {/* Quick Dismiss Actions */}
          {!feedbackGiven && (
            <View style={styles.quickDismissContainer}>
              <Button
                mode='outlined'
                onPress={() => handleDismissWithReason('not_interested')}
                style={styles.quickDismissButton}
                labelStyle={styles.quickDismissLabel}
                contentStyle={styles.quickDismissContent}
                compact
              >
                Not Interested
              </Button>
              <Button
                mode='outlined'
                onPress={() => handleDismissWithReason('wrong_difficulty')}
                style={styles.quickDismissButton}
                labelStyle={styles.quickDismissLabel}
                contentStyle={styles.quickDismissContent}
                compact
              >
                Wrong Level
              </Button>
              <Button
                mode='outlined'
                onPress={() => handleDismissWithReason('no_time')}
                style={styles.quickDismissButton}
                labelStyle={styles.quickDismissLabel}
                contentStyle={styles.quickDismissContent}
                compact
              >
                No Time
              </Button>
            </View>
          )}

          {/* AI Reasoning and Analytics */}
          <View>
            {analyticsData && (
              <View style={styles.toggleButtons}>
                <Button
                  mode='text'
                  onPress={handleAnalyticsToggle}
                  style={styles.explanationToggle}
                  labelStyle={styles.explanationToggleLabel}
                  icon={TrendingUp}
                  compact
                >
                  {isAnalyticsVisible ? 'Hide Stats' : 'Stats'}
                </Button>
              </View>
            )}

            {showExplanation && (
              <RecommendationExplanation
                explanation={recommendation.reasoning}
                confidence={recommendation.confidence}
                {...(onPreferenceUpdate && { onPreferenceUpdate })}
              />
            )}

            {isAnalyticsVisible && analyticsData && (
              <RecommendationAnalytics
                data={analyticsData}
                recommendation={recommendation}
              />
            )}
          </View>

          {/* Separator between quick dismiss and primary actions */}
          {!feedbackGiven && <View style={styles.actionSeparator} />}

          {/* Primary Actions */}
          {!feedbackGiven && (
            <View style={styles.primaryActionsContainer}>
              <Button
                mode='outlined'
                onPress={() => handleFeedback('negative', 3, 'maybe_later')}
                style={styles.primaryActionButton}
                labelStyle={styles.primaryActionLabel}
                contentStyle={styles.primaryActionContent}
                icon={Clock}
              >
                Maybe Later
              </Button>
              <Button
                mode='contained'
                onPress={() => handleFeedback('positive', 5)}
                style={styles.primaryActionButton}
                labelStyle={styles.primaryActionLabel}
                contentStyle={styles.primaryActionContent}
                icon={ThumbsUp}
              >
                Try Now
              </Button>
            </View>
          )}

          {/* Feedback Message */}
          {feedbackGiven && (
            <View style={styles.feedbackContainer}>
              <Text
                variant='bodySmall'
                style={[
                  styles.feedbackText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Thanks for your feedback! We'll improve future recommendations.
              </Text>
              {dismissalReason && (
                <Text
                  variant='bodySmall'
                  style={[
                    styles.feedbackReason,
                    { color: theme.colors.primary },
                  ]}
                >
                  Reason: {dismissalReason.replace(/_/g, ' ')}
                </Text>
              )}
            </View>
          )}
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  content: {
    paddingVertical: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contentType: {
    fontWeight: '600',
    fontSize: 11,
  },
  contentContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  thumbnail: {
    width: 80,
    height: 60,
    borderRadius: 8,
    marginRight: 12,
  },
  textContent: {
    flex: 1,
  },
  title: {
    fontWeight: '600',
    marginBottom: 4,
  },
  description: {
    lineHeight: 16,
  },
  metadata: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  metadataLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metadataText: {
    fontSize: 12,
  },
  difficultyChip: {
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  difficultyText: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 12,
  },
  reasoningContainer: {
    marginBottom: 16,
  },
  toggleButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  explanationToggle: {
    alignSelf: 'flex-start',
    marginLeft: -8,
  },
  explanationToggleLabel: {
    fontSize: 12,
  },
  inlineWhyToggle: {
    marginLeft: 8,
    alignSelf: 'center',
  },
  inlineWhyToggleLabel: {
    fontSize: 11,
  },
  actionSeparator: {
    height: 1,
    backgroundColor: '#00000014',
    marginTop: 8,
    marginBottom: 8,
  },
  performanceChip: {
    height: 20,
    marginLeft: 8,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  performanceChipText: {
    fontSize: 9,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 11,
  },
  actionIcon: {
    margin: 0,
  },
  // Quick Dismiss Actions (above Why this?)
  quickDismissContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 8,
    marginBottom: 16,
    paddingHorizontal: 2,
  },
  quickDismissButton: {
    flex: 0,
    minWidth: 80,
    maxWidth: 100,
    minHeight: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quickDismissLabel: {
    fontSize: 9,
    lineHeight: 12,
    textAlign: 'center',
    paddingHorizontal: 0,
    fontWeight: '500',
    alignSelf: 'center',
    textAlignVertical: 'center',
  },
  quickDismissContent: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 8,
  },
  // Primary Actions (Maybe Later & Try Now)
  primaryActionsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  primaryActionButton: {
    flex: 0,
    minWidth: 120,
    maxWidth: 140,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryActionLabel: {
    fontSize: 11,
    lineHeight: 14,
    textAlign: 'center',
    paddingHorizontal: 6,
    fontWeight: '600',
    alignSelf: 'center',
    flex: 1,
    textAlignVertical: 'center',
  },
  primaryActionContent: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 8,
  },
  // Legacy styles (kept for compatibility)
  actions: {
    gap: 12,
  },
  dismissOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 6,
    justifyContent: 'flex-start',
    paddingHorizontal: 2,
  },
  dismissButton: {
    flex: 0,
    minWidth: 100,
    maxWidth: 120,
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    flex: 0,
    minWidth: 100,
    maxWidth: 120,
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContent: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  actionButtonLabel: {
    fontSize: 10,
    lineHeight: 13,
    textAlign: 'center',
    paddingHorizontal: 4,
    flexWrap: 'wrap',
    fontWeight: '500',
    alignSelf: 'center',
    flex: 1,
    textAlignVertical: 'center',
  },
  primaryButton: {
    backgroundColor: '#4ECDC4',
  },
  primaryButtonLabel: {
    fontSize: 10,
    color: 'white',
    lineHeight: 13,
    textAlign: 'center',
    paddingHorizontal: 4,
    fontWeight: '500',
    alignSelf: 'center',
    flex: 1,
    textAlignVertical: 'center',
  },
  feedbackContainer: {
    alignItems: 'center',
    padding: 8,
  },
  feedbackText: {
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 4,
  },
  feedbackReason: {
    textAlign: 'center',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
});
